import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { getCustomerById } from '@/api/customers'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CustomerInfoCard } from '@/components/customer/CustomerInfoCard'
import { SubscriptionCard } from '@/components/customer/SubscriptionCard'
import { CustomerNotesCard } from '@/components/customer/CustomerNotesCard'
import { CustomerDeliveriesCard } from '@/components/customer/CustomerDeliveriesCard'
import { CustomerInvoicesCard } from '@/components/customer/CustomerInvoicesCard'
import { CustomerInvoicingCard } from '@/components/customer/CustomerInvoicingCard'
import { getCustomerStartDate } from '@/lib/customer'
import type { Customer, Note, Subscription } from '@/api/types'

export function CustomerDetailPage(): React.ReactElement {
  const { customerId } = useParams()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [invoicesVersion, setInvoicesVersion] = useState(0)

  const hasError = errorMessage !== null

  useEffect(() => {
    const id = Number(customerId)

    if (!Number.isFinite(id)) {
      setErrorMessage('Ogiltigt kund-id.')
      return
    }

    const loadCustomer = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const customerData = await getCustomerById(id)
        setCustomer(customerData)
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          setErrorMessage('Kunden hittades inte.')
          return
        }

        setErrorMessage('Kunde inte hämta kundsidan just nu.')
      } finally {
        setIsLoading(false)
      }
    }

    void loadCustomer()
  }, [customerId])

  const handleSubscriptionChanged = (subscription: Subscription): void => {
    setCustomer((current) => (current ? { ...current, subscription } : current))
  }

  const reloadCustomer = async (): Promise<void> => {
    if (!customer) {
      return
    }

    try {
      const customerData = await getCustomerById(customer.id)
      setCustomer(customerData)
    } catch {
      return
    }
  }

  const handleSubscriptionToggled = async (subscription: Subscription): Promise<void> => {
    handleSubscriptionChanged(subscription)

    await reloadCustomer()
  }

  const handleWithdrawalChanged = async (): Promise<void> => {
    setInvoicesVersion((version) => version + 1)

    await reloadCustomer()
  }

  const handleNoteAdded = (note: Note): void => {
    setCustomer((current) => (current ? { ...current, notes: [note, ...current.notes] } : current))
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-serif text-primary">Kundsida</h1>
        <Button
          asChild
          variant="outline"
          className="rounded-none"
        >
          <Link to="/customers">Tillbaka</Link>
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laddar kund...</p>}

      {hasError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {!isLoading && !hasError && customer && (
        <>
          <CustomerInfoCard
            customer={customer}
            onCustomerChanged={setCustomer}
          />

          <SubscriptionCard
            customerId={customer.id}
            subscription={customer.subscription}
            shipments={customer.shipments}
            startedAt={getCustomerStartDate(customer)}
            onSubscriptionChanged={handleSubscriptionChanged}
            onWithdrawalChanged={handleWithdrawalChanged}
          />

          <CustomerDeliveriesCard
            customerId={customer.id}
            subscription={customer.subscription}
            shipments={customer.shipments}
            deliveryPlan={customer.delivery_plan}
            isVerified={customer.validation_status === 'verified'}
            onSubscriptionChanged={handleSubscriptionToggled}
            onDeliveriesChanged={handleWithdrawalChanged}
          />

          <CustomerInvoicingCard
            customer={customer}
            onSubscriptionChanged={handleSubscriptionToggled}
          />

          <CustomerInvoicesCard
            key={invoicesVersion}
            customerId={customer.id}
            identityNumber={customer.identity_number}
          />

          <CustomerNotesCard
            customerId={customer.id}
            notes={customer.notes}
            onNoteAdded={handleNoteAdded}
          />
        </>
      )}
    </div>
  )
}
